import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckMarkIcon } from '@components/icons/check-mark-icon';
import { ErrorMarkIcon } from '@components/icons/error-mark-icon';
import { DocsVerificationStatusEnum } from '@models/enums';
import { useLazyGetVerificationByIdQuery } from '@modules/dashboard/api/verifications-api';
import type { RootState } from '@store/root-reducer';

import { VERIFICATION_POLL_INTERVAL_MS } from './constants';

type SdkOptions = NonNullable<RootState['docsVerification']['sdkOptions']>;

type UseOrganizationVerificationParams = {
    verificationId?: string;
    organizationId?: string;
    isInDashboard: boolean;
    isCustomPartner?: boolean;
    sdkOptions?: SdkOptions;
};

export const useOrganizationVerification = ({
    verificationId,
    organizationId,
    isInDashboard,
    isCustomPartner,
    sdkOptions,
}: UseOrganizationVerificationParams) => {
    const { t } = useTranslation();
    const [getVerificationById, { data }] = useLazyGetVerificationByIdQuery();

    useEffect(() => {
        if (verificationId && organizationId && !isInDashboard && !isCustomPartner) {
            getVerificationById({
                organizationId,
                verificationId,
            });
        }
    }, [getVerificationById, organizationId, verificationId, isInDashboard, isCustomPartner]);

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | undefined;

        if (!isCustomPartner && organizationId && verificationId) {
            interval = setInterval(() => {
                if (
                    data?.status === DocsVerificationStatusEnum.PENDING ||
                    data?.status === DocsVerificationStatusEnum.IN_PROGRESS
                ) {
                    if (!isInDashboard) {
                        getVerificationById({
                            organizationId,
                            verificationId,
                        });
                    }
                } else if (interval) {
                    clearInterval(interval);
                }
            }, VERIFICATION_POLL_INTERVAL_MS);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [
        data?.status,
        getVerificationById,
        organizationId,
        verificationId,
        isInDashboard,
        isCustomPartner,
    ]);

    const actualIcon = useMemo(() => {
        switch (data?.status) {
            case DocsVerificationStatusEnum.VERIFIED:
                return <CheckMarkIcon />;
            case DocsVerificationStatusEnum.FAILED:
                return <ErrorMarkIcon />;
            default:
                return <CheckMarkIcon />;
        }
    }, [data?.status]);

    const actualTitle = useMemo(() => {
        switch (data?.status) {
            case DocsVerificationStatusEnum.VERIFIED:
                return sdkOptions?.settings?.finish_screen_settings?.enabled
                    ? (sdkOptions?.settings?.finish_screen_settings?.title ?? t('common.thankYou'))
                    : t('common.thankYou');
            case DocsVerificationStatusEnum.FAILED:
                return t('docVerificationWidget.finish.title.failed');
            default:
                return sdkOptions?.settings?.finish_screen_settings?.enabled
                    ? (sdkOptions?.settings?.finish_screen_settings?.title ?? t('common.thankYou'))
                    : t('common.thankYou');
        }
    }, [
        data?.status,
        sdkOptions?.settings?.finish_screen_settings?.title,
        sdkOptions?.settings?.finish_screen_settings?.enabled,
        t,
    ]);

    return {
        actualIcon,
        actualTitle,
    };
};
